/*
  Experience Editor Container Component

*/
import React, { Component } from 'react';
import styled from '@emotion/styled';

import { withRouter, Route } from 'react-router-dom';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import ReactSpinner from 'react-spinjs-fix';
import { DEFAULT_OPTS as SPINNER_OPTIONS } from 'views/anyguide/helpers/spinner';
import { titleize } from 'lib/string_utils';

import {
  FormView,
  EditorContainer,
  LoadingOverlay,
  Notice,
} from 'views/anyguide/tours/edit/TourEditStyles';

import LeavingPrompt from 'views/anyguide/tours/edit/components/forms/shared/LeavingPrompt';
import FormLoading from 'views/anyguide/tours/edit/components/forms/shared/FormLoading';

import { Theme } from 'ui_system/theme';
/* Non redux dash naviagtion actions */

import { TimeSlotErrorViewModal } from 'views/anyguide/tours/edit/TimeSlotErrorViewModal';
import { t } from 'services/translation';

// Redux actions */
import {
  fetchExperience,
  updateExperience,
  selectStep,
  toggleMobileMenu,
  toggleChangesModal,
  updateTourStepForm,
} from 'data_system/resource_portal/actions/experienceActions';

import { fetchResource } from 'data_system/resource_portal/actions/resourceActions';
import NavigationBar from '../../../components/NavigationBar';
import Tabs from './tabs/Tabs';
import PhotosContainer from './photos/PhotosContainer';
import DetailsContainer from './details/DetailsContainer';
import AboutContainer from './about/AboutContainer';

const Wrapper = styled('div')`
  line-height: 120%;
`;

class ExperienceEdit extends Component {
  constructor() {
    super();
    this.saveAndExit = false;
    this.STEP_SEQUENCE = ['about', 'details', 'photos'];
  }

  componentDidMount = () => {
    const {
      fetchExperience: dispatchFetchExperience,
      user,
      toggleChangesModal: dispatchToggleChangesModal,
      fetchResource: dispatchFetchResource,
      resource,
      resourceFetching,
    } = this.props;
    const {
      data: { id: resourceId },
    } = user;

    dispatchToggleChangesModal(false);
    const { id } = this.props.match.params;
    dispatchFetchExperience(id, resourceId);
    if (!(resource.id || resourceFetching)) {
      dispatchFetchResource();
    }
  };

  nextStepInSequence = (step) => {
    const i = this.STEP_SEQUENCE.indexOf(step);
    return i > -1 && i < this.STEP_SEQUENCE.length
      ? this.STEP_SEQUENCE[i + 1]
      : false;
  };

  handleExperienceFormSave = (args) => {
    const { step, apiValues } = args;
    const {
      experience,
      updateExperience: dispatchUpdateExperience,
      continueToStep,
      allStepsCompleted,
      user,
    } = this.props;

    const {
      data: { id: resourceId },
    } = user;

    // Update values for backend
    apiValues.id = experience.id;
    apiValues.resourceId = resourceId;

    // Handle step navigation - passed to action
    let nextLocation;
    // When steps are in progress - progress to next step on save
    if (!allStepsCompleted) nextLocation = this.nextStepInSequence(step);
    // Override if nav from Save / Discard modal
    if (continueToStep) nextLocation = continueToStep;
    // Override if Saving & Exiting
    if (this.saveAndExit) {
      nextLocation = '/experiences';
    }

    // Update experience and navigate
    dispatchUpdateExperience(apiValues, nextLocation);

    window.scrollTo(0, 0);
  };

  // Save and exit action (nav bar)
  handleSaveAndExit = () => {
    const { tourStepForm } = this.props;
    this.saveAndExit = true;
    tourStepForm.submitForm().then((_res) => {
      this.saveAndExit = false;
    });
  };

  /*
    Save / Discard / Close  - UnSaved Changes Modal Action
  */
  handleLeavingAction = (action) => {
    const {
      tourStepForm,
      selectStep: dispatchSelectStep,
      continueToStep,
      toggleChangesModal: dispatchToggleChangesModal,
      toggleMobileMenu: dispatchToggleMobileMenu,
      updateTourStepForm: dispatchUpdateTourStepForm,
      changesModalRedirectUrl,
      history,
      experience,
    } = this.props;

    const { isMobile } = Theme.mobile;

    dispatchToggleChangesModal(false);

    if (action === 'save') {
      // Save
      tourStepForm.submitForm();
      if (isMobile) {
        setTimeout(() => {
          dispatchToggleMobileMenu();
          history.push(this.experienceEditUrl(experience));
        }, 0);
      }
    } else {
      dispatchUpdateTourStepForm(tourStepForm.initialValues);
      if (isMobile) {
        setTimeout(() => {
          dispatchToggleMobileMenu();
          history.push(this.experienceEditUrl(experience));
        }, 0);
      } else if (changesModalRedirectUrl) {
        history.push(changesModalRedirectUrl);
      } else {
        dispatchSelectStep(continueToStep);
      }
    }
  };

  handleBackNav = () => {
    const { isMobile } = Theme.mobile;
    const {
      history,
      tourStepForm,
      toggleChangesModal: dispatchToggleChangesModal,
      toggleMobileMenu: dispatchToggleMobileMenu,
      mobileTabsMenuToggled,
    } = this.props;

    if (tourStepForm) {
      dispatchToggleChangesModal(true, '/experiences');
    } else if (isMobile && !mobileTabsMenuToggled) {
      dispatchToggleMobileMenu();
    } else {
      history.push('/experiences');
    }
  };

  experienceEditUrl = (experience) => {
    return `/experiences/${experience.id}/edit`;
  };

  renderForm = () => {
    const {
      experience,
      experienceFetching,
      noticeMessage,
      mobileTabsMenuToggled,
      brand,
    } = this.props;
    const { linkColor } = brand;
    if (!experience || experienceFetching) {
      return (
        <FormView>
          <FormLoading />
        </FormView>
      );
    }
    const commonProps = {
      handleSubmit: this.handleExperienceFormSave,
      i18n: this.props.i18n,
    };

    /* Routes (React Router) to different steps in the tour/experience editor */
    return (
      <FormView mobileTabsMenuToggled={mobileTabsMenuToggled}>
        <Notice
          type={noticeMessage && noticeMessage.type}
          brandColor={linkColor}
          show={noticeMessage}
        >
          {noticeMessage && noticeMessage.msg}
        </Notice>
        <Route
          path={`${this.props.match.path}/about`}
          render={(props) => <AboutContainer {...props} {...commonProps} />}
        />
        <Route
          path={`${this.props.match.path}/details`}
          render={(props) => <DetailsContainer {...props} {...commonProps} />}
        />
        <Route
          path={`${this.props.match.path}/photos`}
          render={(props) => <PhotosContainer {...props} {...commonProps} />}
        />
      </FormView>
    );
  };

  /* Opacity spinner overlay */
  renderLoadingOverlay = () => {
    SPINNER_OPTIONS.radius = 20;
    return (
      <LoadingOverlay>
        <ReactSpinner config={SPINNER_OPTIONS} />
      </LoadingOverlay>
    );
  };

  render = () => {
    const {
      step,
      experience,
      tourStepForm,
      experienceUpdating,
      acknowledgeExperienceErrors,
      experienceErrors,
      experienceFetching,
      mobileTabsMenuToggled,
      showChangesModal,
      brand,
    } = this.props;
    const { linkColor } = brand;
    let actionText;
    if (!mobileTabsMenuToggled) {
      actionText = tourStepForm ? t('SAVE_AND_EXIT') : t('EXIT');
    }
    return (
      <Wrapper>
        {(experienceFetching || experienceUpdating) &&
          this.renderLoadingOverlay()}
        {experienceErrors && experienceErrors.length > 0 && (
          <TimeSlotErrorViewModal
            error={experienceErrors[0]}
            onClose={acknowledgeExperienceErrors}
          />
        )}
        <LeavingPrompt
          brandColor={linkColor}
          showChangesModal={showChangesModal}
          onClose={this.handleLeavingAction}
        />
        <NavigationBar
          backText={titleize(t('BACK'))}
          onBack={this.handleBackNav}
          title={experience && experience.name}
          onAction={tourStepForm ? this.handleSaveAndExit : this.handleBackNav}
          actionText={actionText}
        />
        <EditorContainer gridTemplateColumns="285px 1fr">
          <Tabs
            mobileTabsMenuToggled={mobileTabsMenuToggled}
            selectedStep={step}
            i18n={this.props.i18n}
          />
          {this.renderForm()}
        </EditorContainer>
      </Wrapper>
    );
  };
}

const mapStateToProps = (state) => {
  const { experienceReducer, auth, initialProps, resource } = state;
  return {
    noticeMessage: experienceReducer.noticeMessage,
    previewable: experienceReducer.previewable,
    mobileTabsMenuToggled: experienceReducer.mobileTabsMenuToggled,
    showChangesModal: experienceReducer.showChangesModal,
    changesModalRedirectUrl: experienceReducer.changesModalRedirectUrl,
    tourStepForm: experienceReducer.tourStepForm,
    continueToStep: experienceReducer.continueToStep,
    experience: experienceReducer.experience,
    steps: experienceReducer.steps,
    allStepsCompleted: experienceReducer.allStepsCompleted,
    experienceFetching: experienceReducer.experienceFetching,
    experienceUpdating: experienceReducer.experienceUpdating,
    user: auth.user,
    brand: initialProps.brand,
    resource: resource.resource,
    resourceFetching: resource.resourceFetching,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchExperience,
      updateExperience,
      selectStep,
      toggleMobileMenu,
      toggleChangesModal,
      updateTourStepForm,
      fetchResource,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ExperienceEdit));
